import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toTimestamp } from "@racoon/lib";

type ShiftValues = {
  departureLocationId?: bigint;
  arrivalLocationId?: bigint;
  departureTime?: Date;
  arrivalTime?: Date;
  engineHours?: number;
  mileage?: number;
};
// Volume is not here: it is always the jobs' fact volume, never typed in.
type RouteValues = {
  weightKg?: number;
  mileage?: number;
  /** Trip times are the route's actual ones; the plan is left as planned. */
  factStartAt?: Date;
  factEndAt?: Date;
  destination?: ConnectProto.Location;
};

/**
 * `from`: the source a value was picked from off the field's right-click menu,
 * so its border keeps that source's colour. A value without one was typed in.
 */
type From<V> = Partial<Record<keyof V, FieldSource>>;
type Picked<V> = V & { from?: From<V> };
export type ShiftPatch = Picked<ShiftValues>;
export type RoutePatch = Picked<RouteValues>;
export type ShiftPatchKey = keyof ShiftValues;
export type RoutePatchKey = keyof RouteValues;

const withFrom = <V extends object>(values: Picked<V>, from: From<V>): Picked<V> => {
  const { from: _drop, ...rest } = values;
  return Object.keys(from).length > 0 ? ({ ...rest, from } as Picked<V>) : (rest as Picked<V>);
};

/**
 * Applies `patch`; a value it sets without a `from` reads as typed in again,
 * and one it sets to `undefined` (a field cleared, or reset) is no edit at all.
 */
export const mergePatch = <V extends object>(current: Picked<V>, patch: Picked<V>): Picked<V> => {
  const next = { ...current, ...patch } as Record<string, unknown>;
  const from: From<V> = { ...current.from };
  for (const [key, value] of Object.entries(patch)) {
    if (key === "from") continue;
    delete from[key as keyof V];
    if (value === undefined) delete next[key];
  }
  return withFrom(next as Picked<V>, { ...from, ...patch.from });
};

export type Draft = {
  shift: ShiftPatch;
  crewIds?: bigint[];
  routes: Map<bigint, RoutePatch>;
};

// Shared and never mutated, so a shift without edits keeps stable merged rows.
export const EMPTY_DRAFT: Draft = { shift: {}, routes: new Map() };

export const nonZero = (value: number | undefined) => value || undefined;

/** Where a TTN field's value came from — what its border colour tells the dispatcher. */
export type FieldSource =
  | "manual" // typed into the draft
  | "saved" // on the approved TTN
  | "gps" // the truck's GPS stops
  | "scale" // the scale ticket
  | "sensor" // ignition / CAN engine hours
  | "track" // measured along the GPS track
  | "driver" // the driver's start / finish buttons
  | "plan"; // nothing actual yet: the plan

export type Sourced<T> = { value?: T; source?: FieldSource };

/** A value a field can be set to off its right-click menu, and where it comes from. */
export type SourceOption = { source: FieldSource; value: Date | number | bigint };

/** The options that hold a value, for a field's right-click menu. */
export const optionsOf = (
  ...options: [Date | number | bigint | null | undefined, FieldSource][]
): SourceOption[] =>
  options.flatMap(([value, source]) => (value == null ? [] : [{ value, source }]));

/** The first option that holds a value, with where it came from. */
export const pick = <T>(...options: [T | null | undefined, FieldSource][]): Sourced<T> => {
  for (const [value, source] of options) {
    if (value != null) return { value, source };
  }
  return {};
};

/**
 * For a value stored on an approved TTN: which source it still agrees with —
 * the border's colour. Approval overwrites the fields it fills, so the
 * value's own origin is not recorded anywhere; what can be said is whether the
 * sources still readable today say the same number.
 *
 * `none` — something could be checked and none of it matches, so the value was
 * typed in (or has drifted since approval). `unknown` — there is nothing to
 * check it against: no GPS stop, no track, no scale ticket.
 */
export type Agreement = FieldSource | "none" | "unknown";

type Equal<T> = (a: T, b: T) => boolean;

/** The saved value against every source that can still be read. */
export const agreesWith = <T>(
  saved: T | null | undefined,
  equal: Equal<T>,
  ...options: [T | null | undefined, FieldSource][]
): Agreement => {
  const candidates = options.filter(([value]) => value != null);
  if (saved == null || candidates.length === 0) return "unknown";
  for (const [value, source] of candidates) {
    if (equal(saved, value as T)) return source;
  }
  return "none";
};

/**
 * The agreement of a field whose shown value is the approved one — `undefined`
 * for anything else, so an edit made after approval shows as typed in.
 */
export const savedAgreement =
  (isDone: boolean) =>
  <T>(
    resolved: Sourced<T>,
    equal: Equal<T>,
    ...options: [T | null | undefined, FieldSource][]
  ): Agreement | undefined =>
    isDone && resolved.source === "saved"
      ? agreesWith(resolved.value, equal, ...options)
      : undefined;

// Times are edited and shown to the minute, so they match when they show the
// same minute — not within 60 s of each other, which spans two shown minutes.
const minuteOf = (date: Date) => Math.floor(date.getTime() / 60_000);
export const sameTime = (a: Date, b: Date) => minuteOf(a) === minuteOf(b);
export const sameId = (a: bigint, b: bigint) => a === b;
/** Mileage to 100 m, engine hours to the minute, weight to the kilogram. */
export const closeTo = (epsilon: number) => (a: number, b: number) => Math.abs(a - b) <= epsilon;
export const MILEAGE_EPSILON = 0.1;
export const ENGINE_HOURS_EPSILON = 1 / 60;
export const WEIGHT_EPSILON = 0.5;

/**
 * The trip's net weight off the scale ticket. It fills the weight, but an edit wins over it.
 * `||`, not `??`: both are proto3 scalars, so 0 is the unset default, not a weighing.
 */
export const getRouteScaleWeight = (route: ConnectProto.Route) =>
  route.scaleRecord?.nettoWeightKg || route.netto || undefined;

export const ts = (date?: Date) =>
  date ? (toTimestamp(date) as ConnectProto.Route["planEndAt"]) : undefined;

// Routes arrive in id order, which need not be chronological: the shift's plan
// window runs from the earliest planned start to the latest planned end.
const pickPlanned = (
  routes: ConnectProto.Route[],
  field: "planStartAt" | "planEndAt",
  isBetter: (at: number, bestAt: number) => boolean,
) =>
  routes.reduce<ConnectProto.Route | undefined>((best, route) => {
    const at = fromTimestamp(route[field])?.getTime();
    const bestAt = best && fromTimestamp(best[field])?.getTime();
    return at !== undefined && (bestAt === undefined || isBetter(at, bestAt))
      ? route
      : (best ?? route);
  }, undefined);
export const firstPlanned = (routes: ConnectProto.Route[]) =>
  pickPlanned(routes, "planStartAt", (at, bestAt) => at < bestAt);
export const lastPlanned = (routes: ConnectProto.Route[]) =>
  pickPlanned(routes, "planEndAt", (at, bestAt) => at > bestAt);

/**
 * A start after its end — a trip's unload, the shift's return: the TTN cannot be
 * approved with it. Equal is fine.
 */
export const isTimeRangeInverted = (times: { start?: Date; end?: Date } | undefined) =>
  // To the minute, as shown and edited: hidden seconds must not block approval.
  Boolean(times?.start && times.end && minuteOf(times.start) > minuteOf(times.end));

// A cancelled trip was never driven: the TTN neither shows nor counts it.
export const isShownOnTtn = (route: ConnectProto.Route) =>
  route.status !== ConnectProto.RouteStatus.CANCELLED;

// Only for the "every job finished" gate: pseudo and cancelled trips are never
// completed, so they are not waited for. Writes still include them.
export const isRealRoute = (route: ConnectProto.Route) =>
  !route.isPseudo && route.status !== ConnectProto.RouteStatus.CANCELLED;
